import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { FaChevronRight, FaExternalLinkAlt, FaFile, FaFolder, FaTimes, FaUnlink } from 'react-icons/fa';
import { MdAssignmentTurnedIn } from 'react-icons/md';
import { IconWrapper } from '../../components/IconWrapper';
import type { OrderAcceptanceFile, Project } from '../../types/project';
import {
  isSignedIn,
  listDriveItemChildren,
  type DriveItem,
} from '../../utils/graphService';
import {
  downloadOrderAcceptanceOriginal,
  listOrderAcceptanceCopies,
  openOrderAcceptanceOriginal,
  openOrderAcceptanceRedacted,
  syncOrderAcceptanceCopies,
} from './jobCard.service';
import type { OrderAcceptanceCopy } from './jobCard.types';
import { isRedactableOaFile } from './oaRedact';
import { redactOrderAcceptanceCopy } from './oaRedact.service';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import JobCardOaPreviewModal from './JobCardOaPreviewModal';
import { useOpenedOaPreview } from './useOpenedOaPreview';

interface OrderAcceptanceCardProps {
  project: Project;
  onProjectPatch: (patch: Partial<Project>) => Promise<void>;
}

function asLinkedFiles(value: unknown): OrderAcceptanceFile[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const item = row as Record<string, unknown>;
      const id = String(item.id || '').trim();
      const name = String(item.name || '').trim();
      const webUrl = String(item.webUrl || '').trim();
      if (!id || !name) return null;
      return { id, name, webUrl };
    })
    .filter((row): row is OrderAcceptanceFile => !!row);
}

function projectMetadata(project: Project): Record<string, unknown> {
  const meta = project.metadata;
  return meta && typeof meta === 'object' && !Array.isArray(meta) ? { ...meta } : {};
}

function toLinkedFile(item: DriveItem): OrderAcceptanceFile {
  return {
    id: item.id,
    name: item.name,
    webUrl: item.webUrl,
  };
}

const OrderAcceptanceCard: React.FC<OrderAcceptanceCardProps> = ({ project, onProjectPatch }) => {
  const linked = asLinkedFiles(projectMetadata(project).order_acceptance_files);
  const rootFolderId = String(project.onedrive_folder_id || '').trim();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [folderStack, setFolderStack] = useState<Array<{ id: string; name: string }>>([]);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, OrderAcceptanceFile>>({});
  const [saving, setSaving] = useState(false);
  const [copies, setCopies] = useState<OrderAcceptanceCopy[]>([]);
  const [redacting, setRedacting] = useState(false);
  const [redactProgress, setRedactProgress] = useState<string | null>(null);
  const { preview, openingId, openFile, close } = useOpenedOaPreview();

  const currentFolder = folderStack[folderStack.length - 1] || null;
  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const outputs = useMemo(
    () => copies.filter((row) => row.redacted_name && row.redacted_storage_path),
    [copies],
  );
  const busy = saving || redacting;

  const loadCopies = useCallback(async () => {
    try {
      setCopies(await listOrderAcceptanceCopies(project.id));
    } catch {
      setCopies([]);
    }
  }, [project.id]);

  useEffect(() => {
    void loadCopies();
  }, [linked.length, loadCopies]);

  useEffect(() => {
    let cancelled = false;
    isSignedIn()
      .then((ok) => {
        if (!cancelled) setSignedIn(ok);
      })
      .catch(() => {
        if (!cancelled) setSignedIn(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadFolder = async (folderId: string) => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listDriveItemChildren(folderId));
    } catch (err) {
      setItems([]);
      setError(err instanceof Error ? err.message : 'Failed to list folder contents');
    } finally {
      setLoading(false);
    }
  };

  const openPicker = async () => {
    if (!rootFolderId) return;
    const ok = await isSignedIn();
    setSignedIn(ok);
    if (!ok) {
      setError('Please sign in to Microsoft to browse this job\'s OneDrive folder.');
      return;
    }
    setSelected(Object.fromEntries(linked.map((file) => [file.id, file])));
    setFolderStack([{ id: rootFolderId, name: 'Project folder' }]);
    setPickerOpen(true);
    await loadFolder(rootFolderId);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setError(null);
  };

  const toggleFile = (item: DriveItem) => {
    setSelected((current) => {
      const next = { ...current };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = toLinkedFile(item);
      return next;
    });
  };

  const persistFiles = async (files: OrderAcceptanceFile[]) => {
    setSaving(true);
    setError(null);
    try {
      await onProjectPatch({
        metadata: {
          ...projectMetadata(project),
          order_acceptance_files: files,
        },
      });
      await syncOrderAcceptanceCopies(project.id, files);
      await loadCopies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Order Acceptance files');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveSelection = async () => {
    try {
      await persistFiles(selectedList);
      closePicker();
    } catch {
      // Error is shown on the card / modal.
    }
  };

  const unlinkFile = async (id: string) => {
    try {
      await persistFiles(linked.filter((file) => file.id !== id));
    } catch {
      // Error is shown on the card.
    }
  };

  const openStoredCopy = async (
    file: OrderAcceptanceCopy,
    kind: 'original' | 'redacted',
  ) => {
    setError(null);
    try {
      await openFile(
        `${kind}:${file.id}`,
        () => (kind === 'redacted' ? openOrderAcceptanceRedacted(file) : openOrderAcceptanceOriginal(file)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open file');
    }
  };

  const redactLinkedFiles = async () => {
    if (copies.length === 0 || redacting) return;
    setRedacting(true);
    setError(null);
    setRedactProgress('Starting…');
    const skipped: string[] = [];
    try {
      for (let index = 0; index < copies.length; index += 1) {
        const file = copies[index];
        if (!isRedactableOaFile(file.name, file.content_type)) {
          skipped.push(file.name);
          continue;
        }
        setRedactProgress(`${file.name} (${index + 1} of ${copies.length})`);
        const original = await downloadOrderAcceptanceOriginal(file);
        await redactOrderAcceptanceCopy(file, original, (page) => {
          setRedactProgress(`${file.name} · ${page}`);
        });
      }
      await loadCopies();
      if (skipped.length) {
        setError(`Skipped ${skipped.join(', ')} — only PDF and common images can be redacted.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to redact Order Acceptance files');
    } finally {
      setRedacting(false);
      setRedactProgress(null);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdAssignmentTurnedIn}
          title="Order Acceptance (OA)"
          action={(
            <Button
              size="sm"
              variant="outlined"
              disabled={!rootFolderId || busy}
              onClick={() => { void openPicker(); }}
            >
              {linked.length > 0 ? 'Change files' : 'Link files'}
            </Button>
          )}
        />
        {error && !pickerOpen && <Alert color="danger" sx={{ mb: 1.25 }}>{error}</Alert>}
        {!rootFolderId && (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Create or link a project folder in Project Files below, then select the OA documents here.
          </Typography>
        )}
        {rootFolderId && signedIn === false && (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Sign in to Microsoft to browse and attach files from the project folder.
          </Typography>
        )}
        {rootFolderId && linked.length === 0 && (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            No Order Acceptance files linked yet.
          </Typography>
        )}
        <Stack gap={1}>
          {linked.map((file) => {
            const copy = copies.find((row) => row.drive_item_id === file.id);
            return (
              <Box
                key={file.id}
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                  <Box sx={{ color: 'neutral.500', display: 'flex' }}>
                    <IconWrapper icon={FaFile} />
                  </Box>
                  <Typography level="body-sm" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </Typography>
                </Stack>
                <Stack direction="row" gap={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  {copy && (
                    <Button
                      size="sm"
                      variant="outlined"
                      loading={openingId === `original:${copy.id}`}
                      disabled={busy}
                      onClick={() => { void openStoredCopy(copy, 'original'); }}
                      sx={{ minHeight: 40, flex: { xs: 1, sm: 'none' } }}
                    >
                      Open
                    </Button>
                  )}
                  {file.webUrl && (
                    <IconButton
                      size="sm"
                      variant="plain"
                      component="a"
                      href={file.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${file.name} in OneDrive`}
                    >
                      <IconWrapper icon={FaExternalLinkAlt} />
                    </IconButton>
                  )}
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    disabled={busy}
                    onClick={() => { void unlinkFile(file.id); }}
                    aria-label={`Unlink ${file.name}`}
                  >
                    <IconWrapper icon={FaUnlink} />
                  </IconButton>
                </Stack>
              </Box>
            );
          })}
        </Stack>
        {linked.length > 0 && (
          <Stack gap={1} sx={{ mt: 1.5 }}>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              Redact prices to create copies for fitter and labourer job cards. The originals stay here.
            </Typography>
            <Button
              size="sm"
              variant="solid"
              loading={redacting}
              disabled={busy || copies.length === 0}
              onClick={() => { void redactLinkedFiles(); }}
              sx={{ alignSelf: 'flex-start' }}
            >
              Redact prices
            </Button>
            {redactProgress && (
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>{redactProgress}</Typography>
            )}
            <Box>
              <Typography level="title-sm" sx={{ mb: 0.5 }}>Job card copies</Typography>
              {outputs.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  No redacted files yet. Job cards will not show these OA files until you redact prices.
                </Typography>
              ) : (
                <Stack gap={0.75}>
                  {outputs.map((file) => (
                    <Stack
                      key={file.id}
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography level="body-sm" sx={{ wordBreak: 'break-word' }}>
                        {file.redacted_name}
                      </Typography>
                      <Button
                        size="sm"
                        variant="outlined"
                        loading={openingId === `redacted:${file.id}`}
                        disabled={busy}
                        onClick={() => { void openStoredCopy(file, 'redacted'); }}
                        sx={{ minHeight: 40, width: { xs: '100%', sm: 'auto' } }}
                      >
                        Open
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </CardContent>

      <Modal open={pickerOpen} onClose={closePicker}>
        <ModalDialog sx={{ maxWidth: 640, width: '100%', maxHeight: '90vh' }}>
          <ModalClose />
          <DialogTitle>Link Order Acceptance files</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1.25 }}>
              Browse this job's project folder and select the file(s) that are the supplier Order Acceptance.
            </Typography>
            {error && <Alert color="danger" sx={{ mb: 1.25 }}>{error}</Alert>}
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mb: 1.25 }}>
              {folderStack.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  {index > 0 && (
                    <Box sx={{ color: 'neutral.400', display: 'flex', alignItems: 'center' }}>
                      <IconWrapper icon={FaChevronRight} />
                    </Box>
                  )}
                  <Button
                    size="sm"
                    variant={index === folderStack.length - 1 ? 'soft' : 'plain'}
                    onClick={() => {
                      const next = folderStack.slice(0, index + 1);
                      setFolderStack(next);
                      void loadFolder(folder.id);
                    }}
                  >
                    {folder.name}
                  </Button>
                </React.Fragment>
              ))}
            </Stack>
            {selectedList.length > 0 && (
              <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mb: 1.25 }}>
                {selectedList.map((file) => (
                  <Chip
                    key={file.id}
                    size="sm"
                    variant="soft"
                    color="success"
                    endDecorator={
                      <Box
                        component="span"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelected((current) => {
                            const next = { ...current };
                            delete next[file.id];
                            return next;
                          });
                        }}
                        sx={{ display: 'flex', cursor: 'pointer' }}
                      >
                        <IconWrapper icon={FaTimes} />
                      </Box>
                    }
                  >
                    {file.name}
                  </Chip>
                ))}
              </Stack>
            )}
            <Divider sx={{ mb: 1 }} />
            {loading ? (
              <Stack alignItems="center" justifyContent="center" minHeight={160}>
                <CircularProgress size="sm" />
              </Stack>
            ) : items.length === 0 ? (
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                {currentFolder ? 'This folder is empty.' : 'No files found.'}
              </Typography>
            ) : (
              <Stack gap={0.5} sx={{ maxHeight: 360, overflow: 'auto' }}>
                {items.map((item) => {
                  const isFolder = !!item.folder;
                  const checked = !!selected[item.id];
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 0.75,
                        py: 0.75,
                        borderRadius: 'sm',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'neutral.50' },
                      }}
                      onClick={() => {
                        if (isFolder) {
                          setFolderStack((stack) => [...stack, { id: item.id, name: item.name }]);
                          void loadFolder(item.id);
                          return;
                        }
                        toggleFile(item);
                      }}
                    >
                      {!isFolder && (
                        <Checkbox
                          size="sm"
                          checked={checked}
                          onChange={() => toggleFile(item)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      )}
                      <Box sx={{ color: isFolder ? 'warning.500' : 'neutral.500', display: 'flex' }}>
                        <IconWrapper icon={isFolder ? FaFolder : FaFile} />
                      </Box>
                      <Typography level="body-sm" sx={{ flex: 1, minWidth: 0 }}>
                        {item.name}
                      </Typography>
                      {isFolder && (
                        <Box sx={{ color: 'neutral.400', display: 'flex' }}>
                          <IconWrapper icon={FaChevronRight} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
            <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 1.5 }}>
              <Button variant="outlined" color="neutral" onClick={closePicker}>Cancel</Button>
              <Button loading={saving} onClick={() => { void saveSelection(); }}>
                {selectedList.length === 1 ? 'Link 1 file' : `Link ${selectedList.length} files`}
              </Button>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
      <JobCardOaPreviewModal preview={preview} onClose={close} />
    </Card>
  );
};

export default OrderAcceptanceCard;
